import { useGlobals } from '@bwoty-web/ui-kit/hooks';
import {
  useState,
  useEffect,
  useCallback,
  lazy,
  Suspense,
  useRef,
} from 'react';
import { getGalleryImages } from '../../services/gallery';

const Gallery = lazy(() => import('../../common/gallery/Gallery'));

const GalleryView = () => {
  const { baseUrl, siteId, id, itemType, labels, galleryOpen, isAppWebView } =
    useGlobals();

  const [showGallery, setShowGallery] = useState(false);
  const isFirstRender = useRef(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [images, setImages] = useState([]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!showGallery) {
      window.postMessage('galleryClosed');
    }
  }, [showGallery]);

  const fetchImages = useCallback(
    async (idFromEvent, itemTypeFromEvent) => {
      const idToUse = idFromEvent || id;
      const itemTypeToUse = itemTypeFromEvent || itemType;

      try {
        const galleryImages = await getGalleryImages(
          baseUrl,
          siteId,
          idToUse,
          itemTypeToUse,
        );
        setImages(galleryImages);
        setShowGallery(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    [baseUrl, id, itemType, siteId],
  );

  const messageEventHandler = useCallback(
    (event) => {
      if (event.origin === window.origin) {
        if (typeof event.data === 'string') {
          // example 'gallery?itemType=hotel&id=123'
          const [command, query] = event.data.split('?');
          const parsedQuery = Object.fromEntries(new URLSearchParams(query));
          if (command === 'gallery') {
            setShowGallery(false);
            if (parsedQuery.category) {
              setSelectedCategory(parsedQuery.category);
            }
            fetchImages(parsedQuery.id, parsedQuery.itemType);
          }
        }
      }
    },
    [fetchImages],
  );

  useEffect(() => {
    if (typeof window !== 'undefined' && window) {
      window.addEventListener(
        'message',
        (event) => messageEventHandler(event),
        false,
      );

      return window.removeEventListener(
        'message',
        (event) => messageEventHandler(event),
        false,
      );
    }
    return undefined;
  }, [messageEventHandler]);

  useEffect(() => {
    if (galleryOpen === 'true') {
      fetchImages();
    }
  }, [galleryOpen, fetchImages]);

  return (
    <div
      className="gw-gallery-view"
      style={isAppWebView ? { height: 'fit-content', overflow: 'scroll' } : {}}
    >
      {showGallery && images.length > 0 && (
        <Suspense fallback={<div />}>
          <Gallery
            open={showGallery}
            close={isAppWebView ? undefined : () => setShowGallery(false)}
            preselectedCategory={selectedCategory}
            setPreselectedCategory={setSelectedCategory}
            heading={labels.galleryHeaderText}
            galleryCategories={labels.gallerySections}
            images={images}
            siteId={siteId}
            isAppWebView={isAppWebView}
            galleryImageIndexText={labels.galleryImageIndexText}
          />
        </Suspense>
      )}
    </div>
  );
};

export default GalleryView;
